import { get } from 'lodash-es';
import createCachedSelector from 're-reselect';
import { LogService } from '../../../services/log-service';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { EXTERNAL } from '../../../constants/fulfillment-types';
import { REJECTED_STATUS } from '../../../constants/grid-statuses';
import { selectSalesOrderProperty } from '../entities.selectors';

export const selectFulfillmentPlans = (state) => get(state, 'fulfillmentPlans', emptyObject);
export const selectFulfillmentPlanById = (state, props) => get(selectFulfillmentPlans(state, props), props.fulfillmentPlanId);
export const selectFulfillmentPlanProperty = (state, props) => get(selectFulfillmentPlanById(state, props), props.property);
export const selectFulfillmentPlanFulfillmentType = (state, props) => get(selectFulfillmentPlanById(state, props), 'fulfillmentType');
export const selectFulfillmentPlanIsExternal = (state, props) => selectFulfillmentPlanFulfillmentType(state, props) === EXTERNAL;
export const selectFulfillmentPlanShipFromId = (state, props) => selectFulfillmentPlanById(state, props)?.shipFromId;
export const selectFulfillmentPlanFulfillmentProviderId = (state, props) =>
  selectFulfillmentPlanById(state, props)?.sellerFulfillmentProviderId;
export const selectFulfillmentPlanOrderGridStatus = (state, props) => get(selectFulfillmentPlanById(state, props), 'orderGridStatus');
export const selectFulfillmentPlanIsRejected = (state, props) => selectFulfillmentPlanOrderGridStatus(state, props) === REJECTED_STATUS;
export const selectFulfillmentPlanSellerFulfillmentProviderID = (state, props) =>
  get(selectFulfillmentPlanById(state, props), 'sellerFulfillmentProviderId');
export const selectFulfillmentPlanSellerLabelProviderID = (state, props) =>
  get(selectFulfillmentPlanById(state, props), 'sellerLabelProviderId');
export const selectFulfillmentPlanIsInternational = (state, props) =>
  selectFulfillmentPlanById(state, props)?.properties?.isInternational ?? false;
export const selectFulfillmentPlanShipTo = (state, props) => selectFulfillmentPlanById(state, props)?.shipTo;
export const selectFulfillmentPlanServiceId = (state, props) => selectFulfillmentPlanById(state, props)?.serviceId;
export const selectFulfillmentPlanPackingSlipTemplateId = (state, props) => selectFulfillmentPlanById(state, props)?.packingSlipTemplateId;
export const selectFulfillmentPlanPickBatchExceptionType = (state, props) =>
  selectFulfillmentPlanById(state, props)?.pickBatchExceptionInfo?.exceptionType ?? 0;

export const selectFulfillmentPlansByIds = createCachedSelector(
  selectFulfillmentPlans,
  (_, props) => JSON.stringify(props.fulfillmentPlanIds),
  (fulfillmentPlans, fulfillmentPlanIdsJSON) => {
    const fulfillmentPlanIds = JSON.parse(fulfillmentPlanIdsJSON);

    if (fulfillmentPlanIds.length === 0) {
      return emptyArray;
    }

    const result = fulfillmentPlanIds.reduce((acc, fulfillmentPlanId) => {
      if (fulfillmentPlans[fulfillmentPlanId] === undefined) {
        const errMsg = 'We are attempting to find a fulfillmentPlan in entities that appears to be missing.';
        const err = new Error(errMsg);
        LogService.logInfo(err.message, {
          stack: err.stack,
          fulfillmentPlans_Ids: Object.keys(fulfillmentPlans ?? {}),
          fulfillmentPlanId,
        });
        return acc;
      }
      acc.push(fulfillmentPlans[fulfillmentPlanId]);
      return acc;
    }, []);

    return result.length === 0 ? emptyArray : result;
  },
)((_, props) => JSON.stringify(props.fulfillmentPlanIds));

export const selectFulfillmentPlanIdsBySalesOrderIds = createCachedSelector(
  (state) => state,
  (_, props) => JSON.stringify(props.salesOrderIds),
  (state, salesOrderIdsJSON) => {
    const salesOrderIds = JSON.parse(salesOrderIdsJSON);

    if (!salesOrderIds.length) {
      return emptyArray;
    }

    const fulfillmentPlanIds = new Set();
    for (const salesOrderId of salesOrderIds) {
      const fulfillmentPlanIdsFromSalesOrder = selectSalesOrderProperty(state, { salesOrderId, property: 'fulfillmentPlanIds' });
      if (fulfillmentPlanIdsFromSalesOrder) {
        for (const fpId of fulfillmentPlanIdsFromSalesOrder) {
          fulfillmentPlanIds.add(fpId);
        }
      }
    }

    if (fulfillmentPlanIds.size === 0) {
      return emptyArray;
    }

    return Array.from(fulfillmentPlanIds);
  },
)((_, props) => JSON.stringify(props.salesOrderIds));

export const selectFulfillmentPlansBySalesOrderIds = (state, { salesOrderIds }) => {
  const fulfillmentPlanIds = selectFulfillmentPlanIdsBySalesOrderIds(state, {
    salesOrderIds,
  });

  return selectFulfillmentPlansByIds(state, { fulfillmentPlanIds });
};

export const selectFulfillmentPlansItemsByFulfillmentPlanIds = (state, { fulfillmentPlanIds }) => {
  const fulfillmentPlans = selectFulfillmentPlansByIds(state, {
    fulfillmentPlanIds,
  });

  const fulfillmentPlanItems = fulfillmentPlans.reduce((items, fulfillmentPlan) => {
    if (fulfillmentPlan.items.length) {
      items.push(...fulfillmentPlan.items);
    }
    return items;
  }, []);

  if (fulfillmentPlanItems.length === 0) {
    return emptyArray;
  }

  return fulfillmentPlanItems;
};
